import {
    Component,
    Input,
    Output,
    EventEmitter,
    Pipe,
    PipeTransform,
    ViewChild,
    ElementRef,
    AfterViewInit,
    HostListener,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from '@angular/core';
import { ChevronLeft, ChevronRight, Loader, LucideAngularModule } from 'lucide-angular';
import { NgClass, NgStyle } from '@angular/common';

@Pipe({
    name: 'isArray',
    standalone: true,
})
export class IsArrayPipe implements PipeTransform {
    transform(value: any): boolean {
        return Array.isArray(value);
    }
}

export interface ColumnDef {
    key: string;
    label: string;
    /** Minimum column width in pixels. Defaults to max(150, naturalHeaderWidth). */
    minWidth?: number;
    /** Maximum column width in pixels. Defaults to 400. Does not apply to the actions column. */
    maxWidth?: number;
    valueFn?: (row: any) => any;
    type?: 'text' | 'chips' | 'image';
    chipConfig?: {
        classFn?: (value: any, row?: any) => string;
        displayFn?: (value: any, row?: any) => string;
    };
    imageConfig?: {
        width?: string;
        height?: string;
        altFn?: (row: any) => string;
        fallbackIcon?: any;
    };
}

export interface RowAction {
    id: number;
    label?: string | ((row: any) => string);
    tooltip?: any | ((row: any) => string);
    icon?: any | ((row: any) => string);
    iconClass?: string;
    callback?: (row: any) => void;
    buttonClass?: string | ((row: any) => string);
    disabled?: boolean | ((row: any) => boolean);
    show?: boolean | ((row: any) => boolean);
}

export interface PaginationMeta {
    from: number;
    to: number;
    total: number;
    last_page: number;
    current_page: number;
}

@Component({
    selector: 'app-data-table',
    standalone: true,
    imports: [LucideAngularModule, NgClass, NgStyle, IsArrayPipe],
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
})
export class DataTableComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('headerScrollContainer') headerScrollContainer?: ElementRef<HTMLDivElement>;
    @ViewChild('bodyScrollContainer') bodyScrollContainer?: ElementRef<HTMLDivElement>;
    @ViewChild('headerTable') headerTable?: ElementRef<HTMLTableElement>;
    @ViewChild('bodyTable') bodyTable?: ElementRef<HTMLTableElement>;

    splitTableWidth = 0;
    private lastMeasuredTableWidth = 0;
    columnWidths: number[] = [];
    private lastColumnWidthSignature = '';
    private isSyncingScroll = false;
    private measurementRafId: number | null = null;
    private resizeDebounceId: ReturnType<typeof setTimeout> | null = null;

    @Input({ required: true }) columns!: ColumnDef[];
    @Input({ required: true }) data!: any[];
    @Input() meta?: PaginationMeta;
    @Input() pageSize = 15;
    @Input() rowActions: RowAction[] = [];
    @Input() page = 1;
    @Input() loading = false;
    @Input() error: string | null = null;
    @Input() showPageInput = false;
    @Input() stickyHeader = false;

    @Output() pageChange = new EventEmitter<number>();
    @Output() rowAction = new EventEmitter<{ action: RowAction; row: any }>();

    inputPage: string = '';

    get internalMeta(): PaginationMeta {
        if (this.meta) {
            return this.meta;
        }

        const total = this.data?.length ?? 0;
        const safePageSize = this.pageSize > 0 ? this.pageSize : 10;
        const lastPage = Math.ceil(total / safePageSize) || 1;
        const currentPage = Math.min(Math.max(1, this.page), lastPage);

        if (total === 0) {
            return {
                from: 0,
                to: 0,
                total,
                last_page: lastPage,
                current_page: currentPage,
            };
        }

        const from = (currentPage - 1) * safePageSize + 1;
        const to = Math.min(currentPage * safePageSize, total);

        return {
            from,
            to,
            total,
            last_page: lastPage,
            current_page: currentPage,
        };
    }

    get displayData(): any[] {
        if (this.meta) {
            return this.data;
        }

        const safePageSize = this.pageSize > 0 ? this.pageSize : 10;
        const start = (this.internalMeta.current_page - 1) * safePageSize;
        return (this.data || []).slice(start, start + safePageSize);
    }

    get pages(): (number | string)[] {
        return this.getVisiblePages();
    }

    private getVisiblePages(): (number | string)[] {
        const totalPages = this.internalMeta.last_page;
        const currentPage = this.internalMeta.current_page;
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const visiblePages: (number | string)[] = [];

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        if (startPage > 1) {
            visiblePages.push(1);
            if (startPage > 2) {
                visiblePages.push('...');
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            visiblePages.push(i);
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                visiblePages.push('...');
            }
            visiblePages.push(totalPages);
        }

        return visiblePages;
    }

    readonly ChevronLeft = ChevronLeft;
    readonly ChevronRight = ChevronRight;
    readonly Loader = Loader;

    goTo(page: number) {
        if (page >= 1 && page <= this.internalMeta.last_page && page !== this.page) {
            this.inputPage = '';
            this.page = page;
            this.pageChange.emit(page);
        }
    }

    onInputPageChange(event: any) {
        this.inputPage = event.target.value;
    }

    goToInputPage() {
        const value = Number(this.inputPage);
        if (!value || value < 1 || value > this.internalMeta.last_page) {
            return;
        }
        this.goTo(value);
    }

    onRowAction(action: RowAction, row: any, _rowIndex: number) {
        this.rowAction.emit({ action, row });
    }

    shouldShowAction(action: RowAction, row: any): boolean {
        if (typeof action.show === 'boolean') return action.show;
        if (typeof action.show === 'function') return action.show(row);
        return true;
    }

    shouldDisableAction(action: RowAction, row: any): boolean {
        if (typeof action.disabled === 'boolean') return action.disabled;
        if (typeof action.disabled === 'function') return action.disabled(row);
        return false;
    }

    getButtonClass(action: RowAction, row: any): string {
        if (typeof action.buttonClass === 'function') {
            return action.buttonClass(row);
        }
        return action.buttonClass || '';
    }

    getCellValue(col: ColumnDef, row: any, index?: number): any {
        if (col.key === 'rowNumber' && index !== undefined) {
            return (this.internalMeta.from || 0) + index;
        }
        return col.valueFn ? col.valueFn(row) : row[col.key];
    }

    getActionLabel(action: RowAction, row: any): string {
        if (typeof action.label === 'function') {
            return action.label(row);
        }
        return action.label || '';
    }

    getChipClasses(col: ColumnDef, value: any, row?: any): string {
        const baseClasses = 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-full';
        const defaultColorClasses = 'bg-gray-100 text-gray-800';

        if (col.chipConfig?.classFn) {
            const customClasses = col.chipConfig.classFn(value, row);
            return `${baseClasses} ${customClasses || defaultColorClasses}`;
        }
        return `${baseClasses} ${defaultColorClasses}`;
    }

    getChipDisplay(col: ColumnDef, value: any, row?: any): string {
        if (col.chipConfig?.displayFn) {
            return col.chipConfig.displayFn(value, row);
        }
        return value?.toString() || '';
    }

    getImageAlt(col: ColumnDef, row: any): string {
        if (col.imageConfig?.altFn) {
            return col.imageConfig.altFn(row);
        }
        return `Image for ${row.name || row.id || 'item'}`;
    }

    onImageError(event: any, col: ColumnDef): void {
        if (col.imageConfig?.fallbackIcon) {
            event.target.style.display = 'none';
        }
    }

    /**
     * Returns inline style constraints for a regular data column.
     * Not used for the actions column — that is always content-sized.
     */
    getColumnStyle(col: ColumnDef): { [key: string]: string } {
        const styles: { [key: string]: string } = {};
        // min-width: 150px default ensures columns are never squeezed too narrow.
        styles['min-width'] = `${col.minWidth ?? 150}px`;
        // max-width is only applied when the user explicitly sets it on the column;
        // in sticky mode the colgroup handles the max-width via JS measurement.
        if (col.maxWidth != null) {
            styles['max-width'] = `${col.maxWidth}px`;
        }
        return styles;
    }

    goToPage(pageNum: number | string) {
        if (typeof pageNum === 'number') {
            this.goTo(pageNum);
        }
    }

    ngAfterViewInit(): void {
        this.scheduleSplitHeaderMetrics();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Structural changes (new column definitions, mode toggle, loading state change) require a full
        // reset so the colgroup is rebuilt from scratch. Data/page/meta changes do not change column
        // headers, so preserving existing widths avoids a layout flash during pagination.
        const isStructural = !!(
            changes['stickyHeader'] ||
            changes['columns'] ||
            changes['rowActions'] ||
            changes['loading']
        );

        if (isStructural) {
            this.columnWidths = [];
            this.splitTableWidth = 0;
            this.lastMeasuredTableWidth = 0;
            this.lastColumnWidthSignature = '';
        }

        if (isStructural || changes['data'] || changes['page'] || changes['meta']) {
            this.scheduleSplitHeaderMetrics();
        }
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        // Debounce: keep existing column widths while the user is actively resizing (no flash),
        // then re-measure once the resize settles to update splitTableWidth.
        if (this.resizeDebounceId !== null) {
            clearTimeout(this.resizeDebounceId);
        }
        this.resizeDebounceId = setTimeout(() => {
            this.resizeDebounceId = null;
            this.lastMeasuredTableWidth = 0;
            this.scheduleSplitHeaderMetrics();
        }, 100);
    }

    syncHeaderScroll(event: Event): void {
        if (!this.stickyHeader || !this.headerScrollContainer || this.isSyncingScroll) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        this.isSyncingScroll = true;
        this.headerScrollContainer.nativeElement.scrollLeft = target.scrollLeft;
        this.isSyncingScroll = false;
    }

    syncBodyScroll(event: Event): void {
        if (!this.stickyHeader || !this.bodyScrollContainer || this.isSyncingScroll) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        this.isSyncingScroll = true;
        this.bodyScrollContainer.nativeElement.scrollLeft = target.scrollLeft;
        this.isSyncingScroll = false;
    }

    private scheduleSplitHeaderMetrics(): void {
        if (this.measurementRafId !== null) {
            cancelAnimationFrame(this.measurementRafId);
        }

        this.measurementRafId = requestAnimationFrame(() => {
            this.measurementRafId = null;
            this.syncSplitHeaderMetrics();
            // Run one extra frame for async redraw cases (segment/API reload) without creating loops.
            this.measurementRafId = requestAnimationFrame(() => {
                this.measurementRafId = null;
                this.syncSplitHeaderMetrics();
            });
        });
    }

    private syncSplitHeaderMetrics(): void {
        if (!this.stickyHeader || !this.bodyTable) {
            return;
        }

        const bodyTableElement = this.bodyTable.nativeElement;
        const headerTableElement = this.headerTable?.nativeElement;

        // --- Natural header widths (Range API) ---
        // Range.getBoundingClientRect() returns the actual rendered text bounding box,
        // unaffected by how much space the table layout distributed to the cell.
        const headerCells = Array.from(headerTableElement?.querySelectorAll('thead th') ?? []) as HTMLElement[];

        const headerNaturalWidths = headerCells.map((cell) => {
            const range = document.createRange();
            range.selectNodeContents(cell);
            const contentWidth = range.getBoundingClientRect().width;
            const style = window.getComputedStyle(cell);
            return Math.ceil(contentWidth + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight));
        });

        // --- Natural body cell widths (off-screen clone, colgroup removed) ---
        // Placing the clone inside a zero-width wrapper collapses min-width:100% to zero,
        // so table-layout:auto sizes every column to its true max-content width — giving us
        // accurate body content widths regardless of any existing colgroup constraint.
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:0;overflow:hidden;visibility:hidden';
        const measureClone = bodyTableElement.cloneNode(true) as HTMLTableElement;
        measureClone.removeAttribute('style'); // strip Angular [style.width.px] binding
        const cloneColgroup = measureClone.querySelector('colgroup');
        if (cloneColgroup) cloneColgroup.remove();
        wrapper.appendChild(measureClone);
        document.body.appendChild(wrapper);
        void measureClone.offsetWidth; // force reflow before measuring

        const cloneRows = Array.from(measureClone.querySelectorAll('tbody tr')) as HTMLElement[];
        const cloneRowCells = cloneRows.map((row) => Array.from(row.querySelectorAll('td')) as HTMLElement[]);
        const bodyColumnCount = cloneRowCells.reduce((max, cells) => Math.max(max, cells.length), 0);

        const bodyNaturalWidths = Array.from({ length: bodyColumnCount }, (_, index) => {
            let maxWidth = 0;
            for (const cells of cloneRowCells) {
                const cell = cells[index];
                if (!cell) continue;
                maxWidth = Math.max(maxWidth, Math.ceil(cell.getBoundingClientRect().width));
            }
            return maxWidth;
        });

        document.body.removeChild(wrapper);

        // --- Compute final column widths ---
        const maxColumns = Math.max(bodyNaturalWidths.length, headerNaturalWidths.length);
        if (maxColumns > 0) {
            // Last column is the actions column when rowActions are present — always content-sized.
            const actionsColIndex = this.rowActions.length > 0 ? maxColumns - 1 : -1;

            const measuredColumnWidths = Array.from({ length: maxColumns }, (_, index) => {
                const naturalBodyWidth = bodyNaturalWidths[index] ?? 0;
                const naturalHeaderWidth = headerNaturalWidths[index] ?? 0;

                if (index === actionsColIndex) {
                    // Actions: fit to content (header label or button group, whichever is wider).
                    return Math.max(naturalBodyWidth, naturalHeaderWidth);
                }

                const col = this.columns[index];
                // min: at least 150px, and never narrower than the header label itself.
                const minW = col?.minWidth ?? Math.max(150, naturalHeaderWidth);
                // max: 400px hard cap (or user override). Body content drives width up to this.
                const maxW = col?.maxWidth ?? 400;
                return Math.max(minW, Math.min(naturalBodyWidth, maxW));
            });

            const signature = measuredColumnWidths.join(',');
            if (signature && signature !== this.lastColumnWidthSignature) {
                this.lastColumnWidthSignature = signature;
                this.columnWidths = measuredColumnWidths;
            }
        }

        // Take the larger of the table's natural content width and the scroll container's
        // visible width so the table always fills the container when content is narrower
        // (e.g. after resizing back to a wider window from a smaller one).
        const containerWidth = this.bodyScrollContainer?.nativeElement.clientWidth ?? 0;
        const measuredWidth = Math.max(bodyTableElement.scrollWidth, containerWidth);
        if (!measuredWidth || measuredWidth === this.lastMeasuredTableWidth) {
            return;
        }

        this.lastMeasuredTableWidth = measuredWidth;
        this.splitTableWidth = measuredWidth;

        if (this.headerScrollContainer && this.bodyScrollContainer) {
            this.headerScrollContainer.nativeElement.scrollLeft = this.bodyScrollContainer.nativeElement.scrollLeft;
        }
    }

    ngOnDestroy(): void {
        if (this.resizeDebounceId !== null) {
            clearTimeout(this.resizeDebounceId);
        }
        if (this.measurementRafId !== null) {
            cancelAnimationFrame(this.measurementRafId);
        }
    }
}
